<template>
    <q-page-sticky position="bottom-left" :offset="[20, 20]" class="z-max">
        <q-btn
            fab
            icon="menu"
            color="secondary"
            @click="openedDrawer = !openedDrawer"
            :title="ts('openMainMenu')"
            v-if="!openedDrawer"
        />
    </q-page-sticky>
    <q-drawer
        v-model="openedDrawer"
        :breakpoint="1023"
        show-if-above
        :mini="compactSidebar"
        side="left"
        elevated
        class="bg-grey-8 text-white"
        @click.capture="reopenSidebar"
    >
        <q-scroll-area class="fit sidebar" :content-style="{ 'display': 'flex', 'flex-direction': 'column' }">
            <div class="row sidebar-space">
                <div ref="sidebarFull" class="col-12 flex column justify-between">
                    <div ref="sidebarPart1">
                        <q-list class="sidebar-1-padding">
                            <qe-side-bar-item
                                icon="ads_click"
                                :label="ts('selectEvent')"
                                :to="{ name: 'SelectEvent' }"
                            />
                            <qe-side-bar-item
                                icon="dashboard_customize"
                                :label="ts('createEvent')"
                                :to="{ name: 'NewEvent' }"
                            />
                            <qe-side-bar-item
                                icon="cloud"
                                :label="ts('openFromCloud')"
                                :to="{ name: 'CloudEvents' }"
                                v-if="isAuthenticated"
                            />
                            <qe-side-bar-item
                                icon="drive_folder_upload"
                                :label="ts('openFromFile')"
                                :to="{ name: 'ImportEvent' }"
                            />

                            <qe-side-bar-separator />

                            <qe-side-bar-item
                                v-for="event in lastEvents"
                                :key="event.id"
                                icon="emoji_events"
                                :label="event?.title + ' (' + event?.ctlId + ')'"
                                :modified="event?.modified"
                                :to="{ name: 'EventManager', params: { eventId: event.id } }"
                            />

                            <qe-side-bar-item
                                v-if="isArchive"
                                icon="history"
                                :label="ts('recentEvents')"
                                :to="{ name: 'RecentEvents' }"
                            />

                            <qe-side-bar-separator v-if="lastEvents.length > 0" />

                            <qe-side-bar-item
                                icon="settings"
                                :label="ts('settings')"
                                :to="{ name: 'AppSettings' }"
                            />

                            <qe-side-bar-separator v-if="showComplexSeparator" />
                        </q-list>
                    </div>
                    <div ref="sidebarPart2">
                        <q-list class="sidebar-2-padding">
                            <qe-side-bar-item
                                :icon="link.icon"
                                :label="link.title"
                                @click="openBrowser(link.url)"
                                v-for="link in links"
                            />

                            <qe-side-bar-separator />

                            <qe-side-bar-item
                                v-if="isAuthenticated"
                                icon="logout"
                                :label="ts('logout')"
                                @click="emits('logOut')"
                            />

                            <qe-side-bar-item
                                v-else
                                icon="login"
                                :label="ts('login')"
                                @click="emits('logIn')"
                            />

                            <qe-side-bar-item
                                icon="refresh"
                                :label="ts('updateData')"
                                :title="lastDataUpdate
                                ? ts('dataUpdatedAt', { 'ts': ds(new Date(lastDataUpdate), DateFormat.DateTimeLong) })
                                : ts('lastSailingDataUpdatedNever')"
                                @click="emits('syncData')"
                            />

                            <qe-side-bar-item
                                v-if="inDevEnv"
                                icon="developer_mode"
                                :label="ts('dev')"
                                :to="{ name: 'DevNotes' }"
                            />
                        </q-list>
                    </div>
                </div>
            </div>
        </q-scroll-area>
        <div class="absolute" style="bottom: 15px; right: -17px">
            <q-btn
                v-if="!compactSidebar"
                dense
                round
                elevated
                icon="chevron_left"
                class="floating_button text-black"
                :title="ts('showMiniSidebar')"
                @click="compactSidebar = true"
            />
            <q-btn
                v-else
                dense
                round
                elevated
                icon="chevron_right"
                class="floating_button text-black"
                :title="ts('showFullSidebar')"
                @click="compactSidebar = false"
            />
        </div>
    </q-drawer>
</template>

<script setup lang="ts">
import QeSideBarItem from '@qe/qeSideBarItem.vue'
import QeSideBarSeparator from '@qe/qeSideBarSeparator.vue'
import { Defaults, Options } from '@model/Setting'
import EventsRepo from '@repo/EventsRepo'
import SettingsRepo from '@repo/SettingsRepo'
import { DateFormat, ds, ts } from '@/plugins/i18nFormat'
import { useRepo } from 'pinia-orm'
import { computed, onMounted, onUnmounted, ref, useTemplateRef } from 'vue'

const emits = defineEmits(['syncData', 'logIn', 'logOut'])

const sidebarPart1 = useTemplateRef('sidebarPart1')
const sidebarPart2 = useTemplateRef('sidebarPart2')
const sidebarFull = useTemplateRef('sidebarFull')

const openedDrawer = ref(true)
const compactSidebar = ref(false)
const showComplexSeparator = ref(false)

const onWindowResize = () => {
    showComplexSeparator.value = (sidebarPart1.value?.clientHeight ?? 0) + (sidebarPart2.value?.clientHeight ?? 0) + 14
        >= (sidebarFull.value?.clientHeight ?? 0)
}

onMounted(() => {
    window.addEventListener('resize', onWindowResize)
    onWindowResize()
})

onUnmounted(() => {
    window.removeEventListener('resize', onWindowResize)
})

const lastEvents = computed(() => (useRepo(EventsRepo).orderedListLimited()))
const isArchive = computed(() => (useRepo(EventsRepo).moreThanLimited()))
const inDevEnv = computed(() => (import.meta.env.DEV))
const lastDataUpdate = computed(
    () => (useRepo(SettingsRepo).getOption(Options.LastSailingDataUpdated, Defaults.LastSailingDataUpdated)),
)
const isAuthenticated = computed(() => (useRepo(SettingsRepo).getOption(Options.AuthToken, Defaults.AuthToken) !== ''))

const links = [
    {
        icon: 'menu_book',
        title: ts('rules'),
        url: 'https://www.sailing.cz/get-file/files-dir/dokumenty/zpj_2025-2028_20250206_2p.pdf',
    },
    {
        icon: 'menu_book',
        title: ts('nationalRules'),
        url: 'https://www.sailing.cz/get-file/files-dir/dokumenty/cze-national-prescriptions-2025-2028.pdf',
    },
    {
        icon: 'plagiarism',
        title: ts('protestForm'),
        url: 'https://www.sailing.cz/get-file/files-dir/dokumenty/f31_protest_form_czeeng_2017.pdf',
    },
]

function reopenSidebar() {
    if (compactSidebar.value)
        compactSidebar.value = false
}

function openBrowser(url) {
    window.open(url, '_blank')
}
</script>

<style scoped lang="sass">
.sidebar-space
    min-height: 100%
    flex-grow: 1

.floating_button
    z-index: 1000
    background-color: #eee

.sidebar-1-padding
    padding-top: 8px

.sidebar-2-padding
    padding-bottom: 8px
</style>
